import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { 
    Container, 
    TextField, 
    Box, 
    Typography, 
    Button, 
    Alert, 
    Paper, 
    InputAdornment,
    IconButton,
    CssBaseline,
    Divider,
    Link,
    CircularProgress
} from "@mui/material";
import { 
    Person as PersonIcon,
    Email as EmailIcon, 
    Lock as LockIcon, 
    Visibility,
    VisibilityOff,
    ArrowForward,
    School,
    Timer,
    WifiOff,
    Error as ErrorIcon
} from "@mui/icons-material";
import authApi from "../authApi";

const Signup = ({ navbar }) => {
    const [username, setUsername] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [error, setError] = useState("");
    const [errorType, setErrorType] = useState(""); // "username", "email", "password", "confirmPassword", "network", "server"
    const [success, setSuccess] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setErrorType("");
        setSuccess("");
        setLoading(true);

        // Client-side validation first
        if (password !== confirmPassword) {
            setError("Passwords do not match");
            setErrorType("confirmPassword");
            setLoading(false);
            return;
        }

        try {
            const { data } = await authApi.auth.post("/signup", { 
                username, 
                email, 
                password, 
                confirmPassword 
            });
            
            setSuccess("Registration successful! Redirecting to login...");
            setTimeout(() => navigate("/login"), 2000);
        } catch (error) {
            let errorMessage = "An unexpected error occurred";
            let errorFieldType = "server";
            
            if (error.response) {
                // The request was made and the server responded with a status code
                // that falls out of the range of 2xx
                errorMessage = error.response.data.message || errorMessage;
                const fieldFromServer = error.response.data.field;
                
                if (fieldFromServer === "username") {
                    errorFieldType = "username";
                } else if (fieldFromServer === "email") {
                    errorFieldType = "email";
                } else if (fieldFromServer === "password") {
                    errorFieldType = "password";
                    setPassword("");
                    setConfirmPassword("");
                } else if (fieldFromServer === "confirmPassword") {
                    errorFieldType = "confirmPassword";
                    setPassword("");
                    setConfirmPassword("");
                } else {
                    errorFieldType = "server";
                }
            } else if (error.request) {
                // The request was made but no response was received
                errorMessage = "Network error. Please check your connection and try again.";
                errorFieldType = "network";
            } else {
                // Something happened in setting up the request
                errorMessage = "An error occurred while processing your request.";
                errorFieldType = "server";
            }
            
            setError(errorMessage);
            setErrorType(errorFieldType);
        } finally {
            setLoading(false);
        }
    };

    const handleClickShowPassword = (type) => {
        if (type === 'password') {
            setShowPassword(!showPassword);
        } else {
            setShowConfirmPassword(!showConfirmPassword);
        }
    };

    const handleMouseDownPassword = (event) => {
        event.preventDefault();
    };

    // Custom alert based on error type
    const getAlertContent = () => {
        if (success) {
            return (
                <Alert 
                    severity="success" 
                    sx={{ 
                        mb: 3, 
                        borderRadius: 1
                    }}
                >
                    {success}
                </Alert>
            );
        }

        if (!error) return null;

        // Only show general error alert for network or server errors
        // Field-specific errors are shown in the respective fields
        if (["network", "server"].includes(errorType)) {
            let icon = <ErrorIcon />;
            let severity = "error";
            let action = null;

            if (errorType === "network") {
                icon = <WifiOff />;
                action = (
                    <Button 
                        color="inherit" 
                        size="small" 
                        onClick={handleSubmit}
                    >
                        Retry
                    </Button>
                );
            }

            return (
                <Alert 
                    severity={severity}
                    icon={icon}
                    action={action}
                    sx={{ 
                        mb: 3, 
                        borderRadius: 1,
                        alignItems: 'center'
                    }}
                >
                    {error}
                </Alert>
            );
        }

        return null;
    };

    // Benefits for the right panel
    const benefits = [
        {
            title: "Adaptive Learning",
            description: "Our system adapts to your unique learning patterns"
        },
        {
            title: "Spaced Repetition",
            description: "Scientific approach to maximize retention"
        },
        {
            title: "Progress Tracking",
            description: "Visualize your growth with detailed analytics"
        }
    ];

    return (
        <Box 
            sx={{
                minHeight: 'calc(100vh - 64px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'linear-gradient(120deg, #f0f7ff 0%, #e1f0ff 100%)',
                p: 2
            }}
        >
            {navbar}
            <CssBaseline />

            <Container maxWidth="md">
                <Paper 
                    elevation={3} 
                    sx={{ 
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        borderRadius: 2,
                        overflow: 'hidden',
                        boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                    }}
                >
                    {/* Signup Form Side */}
                    <Box
                        sx={{
                            flex: '1',
                            padding: { xs: 3, sm: 5 },
                            display: 'flex', 
                            flexDirection: 'column',
                            backgroundColor: 'white'
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                mb: 4
                            }}
                        >
                            <School 
                                color="primary" 
                                sx={{ 
                                    fontSize: 40, 
                                    mr: 2 
                                }} 
                            />
                            <Typography 
                                variant="h4" 
                                sx={{ 
                                    fontWeight: 700,
                                    color: 'primary.main',
                                }}
                            >
                                ankiverse.com
                            </Typography>
                        </Box>

                        <Typography 
                            variant="h5" 
                            sx={{ 
                                mb: 1,
                                fontWeight: 600,
                                color: '#333',
                            }}
                        >
                            Create Your Account
                        </Typography>

                        <Typography 
                            variant="body1" 
                            sx={{ 
                                mb: 4,
                                color: 'text.secondary'
                            }}
                        >
                            Join our community of lifelong learners
                        </Typography>

                        {getAlertContent()}

                        <form onSubmit={handleSubmit} style={{ width: '100%' }}>
                            <TextField
                                label="Username"
                                variant="outlined"
                                fullWidth
                                value={username}
                                onChange={(e) => setUsername(e.target.value)}
                                required
                                disabled={loading}
                                error={errorType === "username"}
                                helperText={errorType === "username" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <PersonIcon color={errorType === "username" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Email"
                                variant="outlined"
                                fullWidth
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value.toLowerCase())}
                                required
                                disabled={loading}
                                error={errorType === "email"}
                                helperText={errorType === "email" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <EmailIcon color={errorType === "email" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Password"
                                type={showPassword ? "text" : "password"}
                                variant="outlined"
                                fullWidth
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                required
                                disabled={loading}
                                error={errorType === "password"}
                                helperText={errorType === "password" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color={errorType === "password" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle password visibility"
                                                onClick={() => handleClickShowPassword('password')}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 2 }}
                            />
                            <TextField
                                label="Confirm Password"
                                type={showConfirmPassword ? "text" : "password"}
                                variant="outlined"
                                fullWidth
                                value={confirmPassword}
                                onChange={(e) => setConfirmPassword(e.target.value)}
                                required
                                disabled={loading}
                                error={errorType === "confirmPassword"}
                                helperText={errorType === "confirmPassword" ? error : ""}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <LockIcon color={errorType === "confirmPassword" ? "error" : "primary"} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label="toggle confirm password visibility"
                                                onClick={() => handleClickShowPassword('confirmPassword')}
                                                onMouseDown={handleMouseDownPassword}
                                                edge="end"
                                                disabled={loading}
                                            >
                                                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{ mb: 4 }}
                            />

                            <Button 
                                type="submit" 
                                variant="contained" 
                                fullWidth
                                size="large"
                                disabled={loading}
                                sx={{ 
                                    py: 1.5,
                                    borderRadius: 2,
                                    boxShadow: '0 4px 10px rgba(25, 118, 210, 0.3)',
                                    transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                                    '&:hover': {
                                        transform: 'translateY(-2px)',
                                        boxShadow: '0 6px 15px rgba(25, 118, 210, 0.4)'
                                    },
                                    position: 'relative'
                                }}
                            >
                                {loading ? (
                                    <>
                                        <CircularProgress 
                                            size={24} 
                                            color="inherit" 
                                            sx={{ 
                                                position: 'absolute',
                                                left: '30%'
                                            }}
                                        />
                                        <span style={{ marginLeft: 8 }}>Creating Account...</span>
                                    </>
                                ) : (
                                    <>
                                        Create Account
                                        <ArrowForward sx={{ ml: 1 }} />
                                    </>
                                )}
                            </Button>
                        </form>
                        
                        <Box sx={{ mt: 3, textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">
                                Already have an account?{' '}
                                <Link 
                                    href="/login" 
                                    sx={{ 
                                        fontWeight: 600,
                                        textDecoration: 'none',
                                        '&:hover': { textDecoration: 'underline' }
                                    }}
                                >
                                    Sign in
                                </Link>
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right Side Panel */}
                    <Box
                        sx={{
                            flex: '0.8',
                            bgcolor: 'primary.main',
                            display: { xs: 'none', md: 'flex' },
                            flexDirection: 'column',
                            justifyContent: 'center',
                            alignItems: 'center',
                            padding: 5,
                            background: 'linear-gradient(135deg, #1976d2 20%, #115293 100%)',
                            position: 'relative',
                            overflow: 'hidden'
                        }}
                    >
                        {/* Abstract decorative elements */}
                        <Box sx={{
                            position: 'absolute',
                            width: 300,
                            height: 300,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                            top: -100,
                            right: -100
                        }} />
                        <Box sx={{
                            position: 'absolute',
                            width: 250,
                            height: 250,
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)',
                            bottom: -80,
                            left: -80
                        }} />
                        
                        {/* Content */}
                        <Typography
                            variant="h5"
                            align="center"
                            sx={{
                                fontWeight: 600,
                                color: 'white',
                                mb: 3,
                                zIndex: 2
                            }}
                        >
                            Science-Based Learning
                        </Typography>
                        
                        <Divider sx={{ bgcolor: 'rgba(255,255,255,0.3)', width: '40px', mb: 4 }} />
                        
                        <Box 
                            sx={{ 
                                mb: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            <Timer sx={{ color: 'white', mr: 1 }} />
                            <Typography 
                                variant="body1" 
                                sx={{ 
                                    color: 'white',
                                    fontWeight: 500
                                }}
                            >
                                Key Benefits
                            </Typography>
                        </Box>
                        
                        <Box sx={{ width: '100%', mb: 4 }}>
                            {benefits.map((benefit, index) => (
                                <Box 
                                    key={index} 
                                    sx={{
                                        mb: 3,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        borderRadius: 1,
                                        padding: 2
                                    }}
                                >
                                    <Typography 
                                        variant="subtitle1" 
                                        sx={{ 
                                            color: 'white', 
                                            fontWeight: 600,
                                            mb: 0.5
                                        }}
                                    >
                                        {benefit.title}
                                    </Typography>
                                    <Typography 
                                        variant="body2" 
                                        sx={{ 
                                            color: 'rgba(255,255,255,0.8)'
                                        }}
                                    >
                                        {benefit.description}
                                    </Typography>
                                </Box>
                            ))}
                        </Box>
                        
                        <Typography
                            variant="body2"
                            align="center"
                            sx={{
                                color: 'rgba(255,255,255,0.7)',
                                fontStyle: 'italic',
                                maxWidth: '80%',
                                zIndex: 2
                            }}
                        >
                            Spaced repetition can increase retention by up to 250% compared to traditional study methods.
                        </Typography>
                    </Box>
                </Paper>
            </Container>
        </Box>
    );
};

export default Signup;